import { useState } from 'react';
import {
  Box,
  Button,
  Chip,
  DialogContent,
  DialogTitle,
  FormControl,
  FormLabel,
  Input,
  Modal,
  ModalClose,
  ModalDialog,
  Option,
  Select,
  Textarea,
  Typography,
} from '@mui/joy';
import { Paperclip } from '@phosphor-icons/react';
import { useMutation } from '@tanstack/react-query';
import { AdminDevAPI } from '../../../lib/apiAdmin';
import type { DevTicket, DevTicketPriority, DevTicketType } from '../../../types/admin';
import { useDraftGuard } from '../crm/useDraftGuard';
import { FilePicker } from './TicketAttachments';
import { addDraftFiles, pastedFiles } from './ticketFiles';
import { TicketError } from './ticketShared';

/** Mounted only while creating, so every new ticket starts with a fresh draft. */
export function CreateDevTicketModal({
  boardStatus,
  onClose,
  onCreated,
}: {
  boardStatus: number | null;
  onClose: () => void;
  onCreated: (ticket: DevTicket) => void;
}) {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [type, setType] = useState<DevTicketType>('bug');
  const [priority, setPriority] = useState<DevTicketPriority>('normal');
  const [files, setFiles] = useState<File[]>([]);
  const [fileError, setFileError] = useState<unknown>(null);
  const create = useMutation({
    mutationFn: () => {
      const payload = new FormData();
      payload.append('title', title.trim());
      payload.append('description', description);
      payload.append('ticket_type', type);
      payload.append('priority', priority);
      if (boardStatus != null) payload.append('board_status', String(boardStatus));
      files.forEach((file) => payload.append('files', file));
      return AdminDevAPI.createTicket(payload);
    },
    // Never retry creation automatically: a lost response may already have saved it.
    retry: false,
    onSuccess: (ticket) => onCreated(ticket),
  });
  const busy = create.isPending;
  const dirty = !!(title || description || files.length || type !== 'bug' || priority !== 'normal');
  const guard = useDraftGuard(dirty, busy);

  const addFiles = (incoming: File[]) => {
    if (!incoming.length || busy) return;
    try {
      setFiles(addDraftFiles(files, incoming));
      setFileError(null);
    } catch (error) {
      setFileError(error);
    }
  };

  return (
    <>
      <Modal open onClose={() => guard.request(onClose)}>
        <ModalDialog
          component="form"
          aria-labelledby="create-dev-ticket-title"
          onSubmit={(event) => {
            event.preventDefault();
            if (title.trim() && !busy) create.mutate();
          }}
          onPaste={(event) => addFiles(pastedFiles(event))}
          sx={{
            width: 640,
            maxWidth: 'calc(100vw - 24px)',
            maxHeight: 'calc(100dvh - 24px)',
            p: 0,
            gap: 0,
            overflow: 'hidden',
            borderRadius: 'lg',
          }}
        >
          <Box
            sx={{
              p: { xs: 2, sm: 3 },
              pr: 6,
              borderBottom: '1px solid',
              borderColor: 'divider',
            }}
          >
            <ModalClose
              aria-label="Close new ticket"
              disabled={busy}
              onClick={() => guard.request(onClose)}
            />
            <DialogTitle id="create-dev-ticket-title">New development ticket</DialogTitle>
            <Typography level="body-sm" sx={{ mt: 0.5 }}>
              Describe the work and add files that help explain it.
            </Typography>
          </Box>
          <DialogContent sx={{ p: { xs: 2, sm: 3 }, gap: 2.5 }}>
            <FormControl required disabled={busy}>
              <FormLabel>Title</FormLabel>
              <Input
                autoFocus
                value={title}
                onChange={(event) => setTitle(event.target.value)}
                placeholder="Give this ticket a clear, specific title"
                slotProps={{ input: { maxLength: 255 } }}
              />
            </FormControl>
            <FormControl disabled={busy}>
              <FormLabel>Description</FormLabel>
              <Textarea
                minRows={3}
                maxRows={8}
                value={description}
                onChange={(event) => setDescription(event.target.value)}
                placeholder="What happened? What should happen? Include steps to reproduce or the outcome you need."
              />
            </FormControl>
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
              <FormControl disabled={busy}>
                <FormLabel>Type</FormLabel>
                <Select value={type} onChange={(_, value) => value && setType(value)}>
                  <Option value="bug">Bug</Option>
                  <Option value="feature">Feature</Option>
                  <Option value="improvement">Improvement</Option>
                  <Option value="chore">Chore</Option>
                </Select>
              </FormControl>
              <FormControl disabled={busy}>
                <FormLabel>Priority</FormLabel>
                <Select value={priority} onChange={(_, value) => value && setPriority(value)}>
                  <Option value="normal">Normal</Option>
                  <Option value="low">Low</Option>
                  <Option value="high">High</Option>
                  <Option value="urgent">Urgent</Option>
                </Select>
              </FormControl>
            </Box>
            <Box
              component="section"
              aria-label="Photos and attachments"
              onDragOver={(event) => {
                if (event.dataTransfer.types.includes('Files')) event.preventDefault();
              }}
              onDrop={(event) => {
                if (event.dataTransfer.files.length) {
                  event.preventDefault();
                  event.stopPropagation();
                  addFiles(Array.from(event.dataTransfer.files));
                }
              }}
              sx={{
                p: 2,
                border: '1px dashed',
                borderColor: 'neutral.outlinedBorder',
                borderRadius: 'md',
                bgcolor: 'background.level1',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                <Typography level="title-sm" startDecorator={<Paperclip size={18} />}>
                  Photos & attachments
                </Typography>
                <Chip size="sm" variant="soft">
                  {files.length ? `${files.length} / 10` : 'Optional'}
                </Chip>
              </Box>
              <Typography level="body-xs" sx={{ mb: 1 }}>
                Images, PDFs, documents and logs · Up to 10 files, 50 MB total
              </Typography>
              <FilePicker
                files={files}
                onChange={(next) => {
                  setFiles(next);
                  setFileError(null);
                }}
                disabled={busy}
                label="Choose files"
              />
              <TicketError error={fileError} />
            </Box>
            <TicketError error={create.error} />
          </DialogContent>
          <Box
            sx={{
              px: { xs: 2, sm: 3 },
              py: 2,
              borderTop: '1px solid',
              borderColor: 'divider',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 2,
              flexWrap: 'wrap',
            }}
          >
            <Typography level="body-xs" role="status" sx={{ flex: '1 1 150px' }}>
              {busy
                ? files.length
                  ? 'Creating ticket and uploading files…'
                  : 'Creating ticket…'
                : files.length
                  ? `${files.length} file${files.length === 1 ? '' : 's'} will upload with this ticket`
                  : 'Files upload when you create the ticket.'}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, ml: 'auto' }}>
              <Button
                variant="plain"
                color="neutral"
                disabled={busy}
                onClick={() => guard.request(onClose)}
              >
                Cancel
              </Button>
              <Button type="submit" loading={busy} disabled={!title.trim() || busy}>
                Create ticket
              </Button>
            </Box>
          </Box>
        </ModalDialog>
      </Modal>
      {guard.dialog}
    </>
  );
}
